import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import mainVideoPlaceholder from '../assets/main-video-placeholder.png';
import colors from '../theme/colors';

const HeroSection = () => {
  const [showVideo, setShowVideo] = useState(false);
  return (
    <Box component="section" sx={styles.heroWrapper}>
      {/* Launch Banner */}
      <Box sx={styles.launchBanner}>
        <Container maxWidth="lg">
          <Typography sx={styles.launchText}>
            🚀 We&apos;ve just launched! Enjoy QSAI completely FREE for a
            limited time as part of our launch promotion!
          </Typography>
        </Container>
      </Box>

      {/* Main Hero Content */}
      <Container maxWidth="lg" sx={styles.heroContainer}>
        <Grid container spacing={4} alignItems="center" sx={styles.heroGrid}>
          {/* Left: Video Section */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.videoWrapper}>
              {showVideo ? (
                <iframe
                  src="https://player.vimeo.com/video/1083817341?h=210ae80d80&badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  title="QSAI Introduction"
                  allowFullScreen
                />
              ) : (
                <>
                  <Box
                    component="img"
                    src={mainVideoPlaceholder}
                    alt="QSAI Introduction Video Thumbnail"
                    sx={styles.videoThumbnail}
                  />
                  <Box
                    sx={styles.videoOverlay}
                    onClick={() => setShowVideo(true)}
                  >
                    <PlayCircleIcon sx={styles.playButton} />
                  </Box>
                  <Box sx={styles.videoBadge}>
                    <Typography sx={styles.videoBadgeText}>
                      QSAI Introduction
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Grid>

          {/* Right: Text and CTA */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.heroContent}>
              <Typography variant="h1" sx={styles.heroTitle}>
                Stop Drowning in Quotes!
              </Typography>
              <Typography variant="h2" sx={styles.heroSubtitle}>
                <Box component="span" sx={styles.highlightText}>
                  Streamline Tender Analysis
                </Box>{' '}
                with our AI-Powered QS assistant.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="contained"
                  sx={styles.ctaButton}
                  component={Link}
                  to="/sign-up"
                >
                  Get Started Now For Free
                </Button>

                <Typography sx={styles.ctaSubtext}>
                  No credit card required • Cancel anytime
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Sub-heading Text */}
      <Container maxWidth="lg" sx={styles.subheadingContainer}>
        <Typography sx={styles.subheading}>
          Upload multiple quotes. QSAI will analyze{' '}
          <Box component="span" sx={styles.highlightText}>
            price, programme, risks, and discrepancies,
          </Box>{' '}
          in minutes.
        </Typography>
      </Container>
    </Box>
  );
};

// Styles
const styles = {
  heroWrapper: {
    backgroundColor: '#2b3946',
    color: colors.background.paper,
    position: 'relative',
    overflow: 'hidden',
  },
  launchBanner: {
    width: '100%',
    backgroundColor: colors.secondary.main,
    py: 1.5,
  },
  launchText: {
    color: '#08202a',
    fontWeight: 'bold',
    fontSize: { xs: '14px', md: '16px' },
    textAlign: 'center',
    fontFamily: 'RooneySans, sans-serif',
  },
  heroContainer: {
    py: { xs: 5, md: 8 },
  },
  heroGrid: {
    flexDirection: { xs: 'column-reverse', md: 'row' },
  },
  videoWrapper: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'visible', // Allow glow to be visible
    padding: '0px', // Make video content area smaller
    boxShadow:
      '0 10px 30px rgba(0,0,0,0.3), 0 0 20px 5px rgba(76, 192, 173, 0.4)', // Added glow
    transition: 'transform 0.3s ease, box-shadow 0.3s ease', // Added box-shadow to transition
    aspectRatio: '1 / 1', // Maintain aspect ratio for the video
    '&:hover': {
      transform: 'translateY(-5px) scale(1.02)', // Enhanced hover
      boxShadow:
        '0 15px 35px rgba(0,0,0,0.4), 0 0 25px 8px rgba(76, 192, 173, 0.6)', // Enhanced shadow/glow on hover
    },
  },
  videoThumbnail: {
    width: '100%',
    height: '100%', // Make height 100% to fill the container
    display: 'block',
    objectFit: 'cover', // Cover the container, maintain aspect ratio, crop excess
  },
  videoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.5)',
    },
  },
  playButton: {
    fontSize: '80px',
    color: colors.background.paper,
    opacity: 0.9,
    transition: 'transform 0.3s ease, opacity 0.3s ease',
    '&:hover': {
      opacity: 1,
      transform: 'scale(1.1)',
    },
  },
  videoBadge: {
    position: 'absolute',
    top: 16,
    left: 16,
    backgroundColor: 'rgba(76, 192, 173, 0.9)',
    borderRadius: '4px',
    padding: '4px 12px',
  },
  videoBadgeText: {
    color: colors.background.paper,
    fontSize: '14px',
    fontWeight: 'bold',
  },
  heroContent: {
    textAlign: { xs: 'center', md: 'left' },
  },
  heroTitle: {
    fontSize: { xs: '32px', sm: '40px', md: '48px' },
    fontWeight: 800,
    lineHeight: 1.1,
    marginBottom: 2,
    fontFamily: 'RooneySans, sans-serif',
  },
  heroSubtitle: {
    fontSize: { xs: '24px', sm: '28px', md: '32px' },
    fontWeight: 600,
    lineHeight: 1.3,
    marginBottom: 4,
    fontFamily: 'RooneySans, sans-serif',
    textAlign: 'center',
  },
  highlightText: {
    color: colors.secondary.main,
  },
  ctaButton: {
    backgroundColor: colors.primary.main,
    color: colors.background.paper,
    fontSize: { xs: '16px', md: '18px' },
    fontWeight: 'bold',
    padding: '12px 32px',
    borderRadius: '30px',
    textTransform: 'none',
    boxShadow: '0 4px 14px rgba(76, 192, 173, 0.4)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: colors.primary.dark,
      boxShadow: '0 6px 20px rgba(76, 192, 173, 0.6)',
      transform: 'translateY(-2px)',
    },
  },
  ctaSubtext: {
    color: '#bdbdbd',
    fontSize: '14px',
    marginTop: 2,
    textAlign: 'center',
    maxWidth: '290px',
  },
  subheadingContainer: {
    textAlign: 'center',
    py: { xs: 4, md: 5 },
  },
  subheading: {
    fontSize: { xs: '18px', md: '22px' },
    fontWeight: 400,
    lineHeight: 1.5,
    maxWidth: '800px',
    margin: '0 auto',
    fontFamily: 'RooneySans, sans-serif',
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    margin: '0 auto',
    width: '100%',
  },
};

export default HeroSection;
